
import {
  defineComponent,
  PropType,
  computed,
  ref,
  nextTick,
  watch,
} from 'vue';

import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash';

import { useStore } from '@/store';

import {
  TabItem,
  TabSubitem,
  ParserObject,
  FieldsGroup,
  FieldConfig,
  Property,
  TemplateStructureEnum,
  SelectedStructureSyncPart,
} from '@/types';

import Tooltip from 'primevue/tooltip';

import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import HelpMessage from '@/components/common/HelpMessage.vue';

import StructureEdit from '@/components/template-builder/fields/StructureEdit.vue';
import ButtonGroup from '@/components/template-builder/fields/ButtonGroup.vue';
import DraggableButtonGroup from '@/components/template-builder/fields/DraggableButtonGroup.vue';
import Stepper from '@/components/template-builder/fields/Stepper.vue';
import StepperPxPercentSlider from '@/components/template-builder/fields/StepperPxPercentSlider.vue';
import Color from '@/components/template-builder/fields/Color.vue';
import Spaces from '@/components/template-builder/fields/Spaces.vue';
import FontFamily from '@/components/template-builder/fields/FontFamily.vue';
import Borders from '@/components/template-builder/fields/Borders.vue';
import InputVariables from '@/components/template-builder/fields/InputVariables.vue';
import Code from '@/components/template-builder/fields/Code.vue';
import WysiwygEditor from '@/components/template-builder/fields/WysiwygEditor.vue';
import Link from '@/components/template-builder/fields/Link.vue';
import Dropdown from '@/components/template-builder/fields/Dropdown.vue';
import BoxShadow from '@/components/template-builder/fields/BoxShadow.vue';
import Information from '@/components/template-builder/fields/Information.vue';
import Notice from '@/components/template-builder/fields/Notice.vue';
import InputGroup from '@/components/template-builder/fields/InputGroup.vue';
import SharingType from '@/components/template-builder/fields/SharingType.vue';
import TextareaVariables from '@/components/template-builder/fields/TextareaVariables.vue';
import ItemList from '@/components/template-builder/fields/ItemList.vue';
import TriggerButton from '@/components/template-builder/fields/TriggerButton.vue';
import MultiDropdown from '@/components/template-builder/fields/MultiDropdown.vue';
import SelectionType from '@/components/template-builder/fields/SelectionType.vue';
import ActionType from '@/components/template-builder/fields/ActionType.vue';
import Image from '@/components/template-builder/fields/Image.vue';
import FacebookButtons from '@/components/template-builder/fields/FacebookButtons.vue';
import LineHeight from '@/components/template-builder/fields/LineHeight.vue';
import Font from '@/components/template-builder/fields/Font.vue';
import SocialIconsStyle from '@/components/template-builder/fields/SocialIconsStyle.vue';
import SocialIconsStyleV4 from '@/components/template-builder/fields/SocialIconsStyleV4.vue';
import BackgroundImage from '@/components/template-builder/fields/BackgroundImage.vue';
import CustomHtml from '@/components/template-builder/fields/CustomHtml.vue';
import FormFieldContactProperty from '@/components/template-builder/fields/FormFieldContactProperty.vue';
import FormFieldOptions from '@/components/template-builder/fields/FormFieldOptions.vue';
import FormFieldConditionalDisplay from '@/components/template-builder/fields/FormFieldConditionalDisplay.vue';
import FormFieldLoopConfiguration from '@/components/template-builder/fields/FormFieldLoopConfiguration.vue';
import RichTextingCtas from '@/components/template-builder/fields/RichTextingCtas.vue';
import WhatsappCtas from '@/components/template-builder/fields/WhatsappCtas.vue';
import DatetimePicker from '@/components/template-builder/fields/DatetimePicker.vue';
import Checkbox from '@/components/template-builder/fields/Checkbox.vue';
import PositionGrid from '@/components/template-builder/fields/PositionGrid.vue';
import SocialNetworkInputGroup from '@/components/template-builder/fields/SocialNetworkInputGroup.vue';
import MenuInputGroup from '@/components/template-builder/fields/MenuInputGroup.vue';
import GamePrizesEditor from '@/components/template-builder/fields/GamePrizesEditor.vue';
import GameSlotSymbolsEditor from '@/components/template-builder/fields/GameSlotSymbolsEditor.vue';
import GameSlotCombinationsEditor from '@/components/template-builder/fields/GameSlotCombinationsEditor.vue';
import GameTestButton from '@/components/template-builder/fields/GameTestButton.vue';
import ValidationRules from '@/components/template-builder/fields/ValidationRules.vue';
import GameStockEditor from '@/components/template-builder/fields/GameStockEditor.vue';
import WheelDisplayModeField from '@/components/template-builder/fields/WheelDisplayModeField.vue';
import WheelPointerPositionField from '@/components/template-builder/fields/WheelPointerPositionField.vue';
import { TemplateEditorState, handlePropertyValuesChange, showElementsModal, setEditorViewport } from '@/composables/template-editor/TemplateEditor';
import { getTemplateIframeDocument, isDisplayTemplate } from '../utils/helpers';
import { HIDE_MOBILE_ATTRIBUTE } from '../utils/constants';

export default defineComponent({
  name: 'ConfigurationPanelTabContentFieldGroup',

  components: {
    StructureEdit,
    SpmButton,
    ButtonGroup,
    DraggableButtonGroup,
    Stepper,
    StepperPxPercentSlider,
    Color,
    Font,
    Spaces,
    FontFamily,
    LineHeight,
    Borders,
    InputVariables,
    TextareaVariables,
    Code,
    WysiwygEditor,
    Link,
    Dropdown,
    BoxShadow,
    Information,
    Notice,
    InputGroup,
    Image,
    SharingType,
    ItemList,
    TriggerButton,
    MultiDropdown,
    SelectionType,
    ActionType,
    FacebookButtons,
    SocialIconsStyle,
    SocialIconsStyleV4,
    BackgroundImage,
    CustomHtml,
    FormFieldContactProperty,
    FormFieldOptions,
    FormFieldConditionalDisplay,
    FormFieldLoopConfiguration,
    RichTextingCtas,
    WhatsappCtas,
    DatetimePicker,
    Checkbox,
    PositionGrid,
    SocialNetworkInputGroup,
    MenuInputGroup,
    GamePrizesEditor,
    GameSlotSymbolsEditor,
    GameSlotCombinationsEditor,
    GameTestButton,
    ValidationRules,
    GameStockEditor,
    WheelDisplayModeField,
    WheelPointerPositionField,
    HelpMessage,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    fieldGroup: {
      type: Object as PropType<FieldsGroup>,
      required: true,
    },

    tabItem: {
      type: Object as PropType<TabItem | TabSubitem>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserObject>,
      required: true,
    },

    customClass: {
      type: Object,
      default: null,
    },

    itemGroup: {
      type: Object as PropType<FieldsGroup>,
      required: false,
      default: null,
    },

    subGroupLabel: {
      type: String,
      required: false,
      default: '',
    },

    headerTitle: {
      type: String,
      required: false,
      default: '',
    },

    isSynchronizedPage: {
      type: Boolean,
      required: false,
      default: false,
    },

    nestedTabLabel: {
      type: String,
      required: false,
      default: '',
    },

    parentFieldGroupLabel: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['refresh'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const toggleVersion = computed(() => store.getters['liveEditor/getToggleVersion']);
    const mobileEditMode = computed(() => store.getters['liveEditor/getMobileEditMode']);

    const MOBILE_EDITABLE_LABELS = new Set([
      'templateBuilder.configs.widgetStyles',
      'templateBuilder.configs.column',
      'templateBuilder.configs.line',
      'templateBuilder.configs.pageMargins',
      'templateBuilder.configs.gameWheelStyles',
      'templateBuilder.configs.gameSlotMachineStyles',
      'templateBuilder.configs.gameScratchCardStyles',
    ]);
    const isMobileEditableByParent = !!props.parentFieldGroupLabel && MOBILE_EDITABLE_LABELS.has(props.parentFieldGroupLabel);
    // Show switch only when NOT handled by a parent tab-item
    const isPageMargins = props.fieldGroup.label === 'templateBuilder.configs.pageMargins';
    const isMobileEditable = computed(() => {
      if (isMobileEditableByParent) return false;
      if (isPageMargins) return isDisplayTemplate(TemplateEditorState.template?.type);
      return MOBILE_EDITABLE_LABELS.has(props.fieldGroup.label) || MOBILE_EDITABLE_LABELS.has(props.tabItem.label);
    });
    const isMobileStylesDisabled = computed(() => getTemplateIframeDocument()?.body.getAttribute(HIDE_MOBILE_ATTRIBUTE) === 'true');
    // Apply mobile override when this field group or its parent is whitelisted
    const shouldApplyMobileOverride = computed(() => isMobileEditableByParent || isMobileEditable.value);

    const switchToDesktop = () => {
      if (!mobileEditMode.value) return;
      setEditorViewport('desktop');
    };

    const switchToMobile = () => {
      if (mobileEditMode.value) return;
      setEditorViewport(TemplateEditorState.mobileDevice);
    };

    const wrapSetterForMobile = (setter: Function) => {
      if ((setter as any).isCssSetter) {
        console.log('[MobileOverride] Wrapping setter with mobile=true, isCssSetter:', true);
        return (selector: string, property: Property, ...args: any[]) => {
          console.log('[MobileOverride] Setter called with mobile=true for:', property.name, 'value:', property.value);
          return setter(selector, property, true);
        };
      }
      console.log('[MobileOverride] Setter NOT wrapped (no isCssSetter):', setter.name);
      return setter;
    };

    const wrapGetterForMobile = (getter: Function) => (selector: string, name: string, onlyValue?: any, ...args: any[]) => getter(selector, name, onlyValue, true);

    const applyMobileOverride = (items: FieldConfig[]): FieldConfig[] => {
      console.log('[MobileOverride] applyMobileOverride called for', items.length, 'items, fieldGroup:', props.fieldGroup.label);
      return items.map((item: FieldConfig) => ({
        ...item,
        mobile: true,
        properties: item.properties.map((prop: Property) => ({
          ...prop,
          setters: prop.setters.map((setter: Function) => wrapSetterForMobile(setter)),
          getter: wrapGetterForMobile(prop.getter),
        })),
      }));
    };

    const fieldGroupItems = computed(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _tv = toggleVersion.value;
      let items: FieldConfig[] = [];
      if (props.itemGroup?.items) {
        items = cloneDeep(props.itemGroup.items);
      } else if (props.fieldGroup.items) {
        items = cloneDeep(props.fieldGroup.items).filter((item: any) => {
          if (item.isVisible && item.parentFieldId) {
            return item.isVisible(props.fieldGroup.items, item.parentFieldId);
          }
          return true;
        });
      }
      if (mobileEditMode.value && shouldApplyMobileOverride.value && items.length > 0) {
        return applyMobileOverride(items);
      }
      return items;
    });

    const newParserValues = ref(cloneDeep(props.parserValues));

    // Watch for parser values changes only for fields that have watchParserValues: true
    watch(() => props.parserValues, (newValues) => {
      // Check if any field in this group has watchParserValues enabled
      const hasWatchableFields = fieldGroupItems.value?.some((field: any) => field.watchParserValues === true);
      if (hasWatchableFields) {
        newParserValues.value = cloneDeep(newValues);
      }
    }, { deep: true });

    const backupSelectedStructure = computed(() => store.getters['liveEditor/getBackupSelectedStructure']);
    const selectedStructure = computed(() => store.getters['liveEditor/getSelectedStructure']);
    const selectedSyncElement = computed(() => store.getters['liveEditor/getSelectedSyncElement']);
    const componentInSyncPart = computed(() => {
      if (props.fieldGroup.elementPartType && selectedStructure.value) {
        return selectedStructure.value.syncParts.find((syncPart: SelectedStructureSyncPart) => syncPart.partType === props.fieldGroup.elementPartType);
      }
      return null;
    });
    const hideLoadElementBtn = computed(() => {
      if (selectedSyncElement.value && props.fieldGroup.elementPartType && selectedStructure.value) {
        return selectedStructure.value.syncParts.find((syncPart: SelectedStructureSyncPart) => syncPart.syncId === selectedSyncElement.value.syncElementId);
      }
      return false;
    });
    const canSaveStyle = computed(() => {
      if (!selectedSyncElement.value) {
        return true;
      }
      if (selectedSyncElement.value && selectedSyncElement.value.elementType === TemplateStructureEnum.SECTION && !selectedSyncElement.value.elementPartType) {
        return true;
      }
      return false;
    });

    const handleSetDefaultLabel = (data: { parentFieldId: string; defaultLabel: string }, tabItemLabel: string, fieldGroupLabel: string, subGroupLabel?: string | undefined) => {
      // Only handle label fields
      if (data.parentFieldId !== 'label') return;

      // Find the label field in the fieldGroup
      const labelField = props.fieldGroup.items?.find((item: any) => item.fieldId === 'label');
      if (labelField && labelField.label) {
        // Get the current label value
        const currentPaths = subGroupLabel
          ? (newParserValues.value as any)[tabItemLabel].items[subGroupLabel][fieldGroupLabel][labelField.label]
          : (newParserValues.value as any)[tabItemLabel].items[fieldGroupLabel][labelField.label];

        if (currentPaths?.properties?.[0]) {
          // Always update the label when property changes (automatic label generation)
          currentPaths.properties[0].value = data.defaultLabel;
          // Emit change to update the label
          handlePropertyValuesChange({
            selector: currentPaths.selector,
            properties: currentPaths.properties,
          }, {
            tabItemLabel,
            fieldGroupLabel,
            fieldLabel: labelField.label,
            subGroupLabel,
          });
        }
      }
    };

    const handleSetDefaultPlaceholder = (
      data: { parentFieldId: string; defaultLabel: string },
      tabItemLabel: string,
      fieldGroupLabel: string,
      subGroupLabel?: string | undefined,
    ) => {
      // Only handle placeholder fields
      if (data.parentFieldId !== 'placeholder') return;

      // Find the placeholder field in the fieldGroup
      const placeholderField = props.fieldGroup.items?.find((item: any) => item.fieldId === 'placeholder');
      if (placeholderField && placeholderField.label) {
        // Get the current placeholder value
        const currentPaths = subGroupLabel
          ? (newParserValues.value as any)[tabItemLabel].items[subGroupLabel][fieldGroupLabel][placeholderField.label]
          : (newParserValues.value as any)[tabItemLabel].items[fieldGroupLabel][placeholderField.label];

        if (currentPaths?.properties?.[0]) {
          // Always update the placeholder when property changes (automatic placeholder generation)
          currentPaths.properties[0].value = data.defaultLabel;
          // Emit change to update the placeholder
          handlePropertyValuesChange({
            selector: currentPaths.selector,
            properties: currentPaths.properties,
          }, {
            tabItemLabel,
            fieldGroupLabel,
            fieldLabel: placeholderField.label,
            subGroupLabel,
          });
        }
      }
    };

    const handleSetDefault = (data: { parentFieldId: string; defaultLabel: string }, tabItemLabel: string, fieldGroupLabel: string, subGroupLabel?: string | undefined) => {
      handleSetDefaultLabel(data, tabItemLabel, fieldGroupLabel, subGroupLabel);
      handleSetDefaultPlaceholder(data, tabItemLabel, fieldGroupLabel, subGroupLabel);
    };

    const handleChange = (data: any, tabItemLabel: string, fieldGroupLabel: string, fieldLabel: string, subGroupLabel?: string | undefined) => {
      if (backupSelectedStructure.value
        && ((props.isSynchronizedPage && selectedStructure.value.type === TemplateStructureEnum.PAGE) || componentInSyncPart.value)
        && !selectedSyncElement.value
        && selectedStructure.value
        && !data.init
      ) {
        const elementType = componentInSyncPart.value ? selectedStructure.value.type : TemplateStructureEnum.DESIGN;
        const name = componentInSyncPart.value ? '' : props.headerTitle;

        store.dispatch('liveEditor/showSyncElementWarning', {
          elementId: selectedStructure.value.identifier,
          elementType,
          name,
          elementPartType: props.fieldGroup.elementPartType,
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          acceptCallback: async () => {
            if (data.askConfirmation && data.properties.length > 0) {
              store.dispatch('liveEditor/showConfirmationModal', {
                title: t('confirmation'),
                message: t('templateBuilder.fields.listLayoutConfirmation'),
                acceptCallback: async () => {
                  await handlePropertyValuesChange(data, {
                    tabItemLabel,
                    fieldGroupLabel,
                    fieldLabel,
                    subGroupLabel,
                    nestedTabLabel: props.nestedTabLabel,
                  });

                  store.commit('liveEditor/setConfirmationModal', null);
                  store.commit('liveEditor/setConfirmationModalReturnToOldValue', false);
                  store.commit('liveEditor/setConfirmationModalReturnToOldValueProperty', null);

                  await nextTick();
                  store.commit('liveEditor/refreshConfigurationPanelKey');
                },
                rejectCallback: async () => {
                  data.properties.forEach((property: any) => {
                    // eslint-disable-next-line no-param-reassign
                    property.value = data.oldValue;
                  });
                  store.commit('liveEditor/setConfirmationModalReturnToOldValue', true);
                  store.commit('liveEditor/setConfirmationModalReturnToOldValueProperty', data.properties[0].name);
                  store.commit('liveEditor/setConfirmationModal', null);

                  await nextTick();
                  store.commit('liveEditor/refreshConfigurationPanelKey');
                },
              });
            } else {
              await handlePropertyValuesChange(data, {
                tabItemLabel,
                fieldGroupLabel,
                fieldLabel,
                subGroupLabel,
                nestedTabLabel: props.nestedTabLabel,
              });
              await nextTick();
              store.commit('liveEditor/refreshConfigurationPanelKey');
            }
          },
          rejectCallback: () => {
            store.commit('liveEditor/setSelectedStructure', backupSelectedStructure.value);
            emit('refresh');
          },
        });
      } else {
        const { init } = data;
        if (init) {
          handlePropertyValuesChange(data);
        } else {
          // eslint-disable-next-line no-lonely-if
          if (data.askConfirmation && data.properties.length > 0) {
            store.dispatch('liveEditor/showConfirmationModal', {
              title: t('confirmation'),
              message: t('templateBuilder.fields.listLayoutConfirmation'),
              acceptCallback: () => {
                handlePropertyValuesChange(data, {
                  tabItemLabel,
                  fieldGroupLabel,
                  fieldLabel,
                  subGroupLabel,
                  nestedTabLabel: props.nestedTabLabel,
                });

                store.commit('liveEditor/setConfirmationModal', null);
                store.commit('liveEditor/setConfirmationModalReturnToOldValue', false);
                store.commit('liveEditor/setConfirmationModalReturnToOldValueProperty', null);
              },
              rejectCallback: () => {
                data.properties.forEach((property: any) => {
                  // eslint-disable-next-line no-param-reassign
                  property.value = data.oldValue;
                });
                store.commit('liveEditor/setConfirmationModalReturnToOldValue', true);
                store.commit('liveEditor/setConfirmationModalReturnToOldValueProperty', data.properties[0].name);
                store.commit('liveEditor/setConfirmationModal', null);
              },
            });
          } else {
            handlePropertyValuesChange(data, {
              tabItemLabel,
              fieldGroupLabel,
              fieldLabel,
              subGroupLabel,
              nestedTabLabel: props.nestedTabLabel,
            });
          }
        }
      }
    };

    const enterEditSyncElement = () => {
      const elementType = componentInSyncPart.value ? selectedStructure.value.type : TemplateStructureEnum.DESIGN;
      const name = componentInSyncPart.value ? '' : props.headerTitle;

      store.dispatch('liveEditor/showSyncElementWarning', {
        elementId: selectedStructure.value.identifier,
        elementType,
        name,
        elementPartType: props.fieldGroup.elementPartType,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        acceptCallback: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        rejectCallback: () => {},
      });
    };

    const originCall = computed(() => {
      const source = TemplateEditorState.template?.createdFromCampaign;
      if (!source) return 'templateBuilder';
      return /_automation/.test(source) ? 'automation' : 'newsletter';
    });
    const idCampaign = computed(() => TemplateEditorState.template?.idCampaign ?? null);

    const handleShowSaveElementModal = () => {
      store.commit('liveEditor/showSaveElementModal', {
        selector: selectedStructure.value ? `#${selectedStructure.value.identifier}` : '',
        type: selectedStructure.value ? selectedStructure.value.type : TemplateStructureEnum.WIDGET,
        elementPartType: props.fieldGroup.elementPartType,
        fromSyncElement: !canSaveStyle.value,
      });
    };

    const handleShowElementModal = () => {
      if (selectedStructure.value) {
        const template = getTemplateIframeDocument();
        if (template) {
          const element = template.querySelector(`#${selectedStructure.value.identifier}`);
          const elementSubtype = element?.getAttribute('data-widgettype') || '';
          showElementsModal([selectedStructure.value.type], {
            elementPartType: props.fieldGroup.elementPartType || '',
            elementSubtype,
            fromSyncElement: !!selectedSyncElement.value,
          });
        }
      }
    };

    const getFieldParserValues = (fieldLabel: string, isNested = false) => {
      const tabItems = newParserValues.value[props.tabItem.label]?.items as any;

      if (!tabItems) {
        return undefined;
      }

      if (props.nestedTabLabel) {
        // For nested tabs, nestedTabLabel is the parent tab label
        // and fieldGroup.label is the field group label inside
        const nestedTabItems = tabItems[props.nestedTabLabel];
        if (!nestedTabItems) {
          return undefined;
        }
        const fieldGroup = nestedTabItems[props.fieldGroup.label];
        if (!fieldGroup) {
          return undefined;
        }
        const field = fieldGroup[fieldLabel];
        return field;
      }

      if (isNested && props.subGroupLabel) {
        const subGroup = tabItems[props.subGroupLabel];
        if (!subGroup) {
          return undefined;
        }
        const fieldGroup = subGroup[props.fieldGroup.label];
        if (!fieldGroup) {
          return undefined;
        }
        const field = fieldGroup[fieldLabel];
        return field;
      }

      const fieldGroup = tabItems[props.fieldGroup.label];
      if (!fieldGroup) {
        return undefined;
      }
      const field = fieldGroup[fieldLabel];
      return field;
    };

    // Event handler methods
    const applyMobileOverrideToEventData = (data: any): any => {
      if (mobileEditMode.value && shouldApplyMobileOverride.value && data.properties) {
        return {
          ...data,
          properties: data.properties.map((prop: Property) => ({
            ...prop,
            setters: prop.setters.map((setter: Function) => wrapSetterForMobile(setter)),
            getter: wrapGetterForMobile(prop.getter),
          })),
        };
      }
      return data;
    };

    const onFieldChange = (data: any, fieldLabel: string) => {
      handleChange(applyMobileOverrideToEventData(data), props.tabItem.label, props.fieldGroup.label, fieldLabel);
    };

    const onNestedFieldChange = (data: any, fieldLabel: string) => {
      handleChange(applyMobileOverrideToEventData(data), props.tabItem.label, props.fieldGroup.label, fieldLabel, props.subGroupLabel);
    };

    return {
      t,
      fieldGroupItems,
      mobileEditMode,
      isMobileEditable,
      isMobileStylesDisabled,
      switchToDesktop,
      switchToMobile,
      handleChange,
      handleSetDefault,
      onFieldChange,
      onNestedFieldChange,
      originCall,
      idCampaign,
      componentInSyncPart,
      handleShowSaveElementModal,
      handleShowElementModal,
      hideLoadElementBtn,
      selectedSyncElement,
      canSaveStyle,
      getFieldParserValues,
      enterEditSyncElement,
    };
  },
});
